<template>
    <Container>
        <SectionTitle>{{ $t('More.settings') }}</SectionTitle>
        <SectionWrapper>
            <SettingButton
                v-for="(button, index) in settingsButtons"
                :key="`s-${index}`"
                :icon="button.icon"
                :on-click="button.onClick"
                :to="button.to">
                {{ button.text }}
            </SettingButton>
        </SectionWrapper>
        <SectionTitle class="tw-mt-5">{{ $t('More.application') }}</SectionTitle>
        <SectionWrapper>
            <SettingButton
                v-for="(button, index) in appButtons"
                :key="`a-${index}`"
                :href="button.href"
                :icon="button.icon"
                :on-click="button.onClick"
                :target="button.target"
                :to="button.to">
                {{ button.text }}
            </SettingButton>
        </SectionWrapper>
        <!-- app version -->
        <p class="tw-fixed tw-bottom-safe-area-bottom tw-left-1/2 -tw-translate-x-1/2 tw-pb-4 tw-text-center tw-text-xxs tw-tracking-tighter tw-text-gray-400">v{{ appVersion }}</p>

        <!-- Clear Storage Dialog -->
        <ClearStorageDialog
            v-model="showDialog"
            :is-clearing="isClearing"
            @cancel="cancelClearStorage"
            @confirm="performClearStorage" />

        <!-- Error Dialog -->
        <v-dialog
            v-model="showErrorDialog"
            max-width="420">
            <v-card>
                <v-card-title class="tw-flex tw-items-center tw-gap-3">
                    <v-icon color="red">mdi-alert-circle</v-icon>
                    <span>{{ $t('shared.error') }}</span>
                </v-card-title>
                <v-card-text>
                    {{ $t('More.clearStorageError') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        variant="flat"
                        @click="showErrorDialog = false">
                        {{ $t('shared.ok') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PWA Install Modal -->
        <PWAInstallModal v-model="showPwaInstallModal" />
    </Container>
</template>

<script setup lang="ts">
    // icons
    import NotificationIcon from '@/components/icons/Notification.vue';
    import EmailIcon from '@/components/icons/Email.vue';
    import LogoIcon from '@/components/icons/Logo.vue';
    import LockIcon from '@/components/icons/Lock.vue';
    import PhoneIcon from '@/components/icons/Phone.vue';
    import ClearIcon from '@/components/icons/Clear.vue';
    // components
    import Container from '@/components/shared/Container.vue';
    import SectionTitle from '@/components/more/SectionTitle.vue';
    import SectionWrapper from '@/components/more/SectionWrapper.vue';
    import SettingButton from '@/components/more/SettingButton.vue';
    import ClearStorageDialog from '@/components/more/ClearStorageDialog.vue';
    import PWAInstallModal from '@/components/PWAInstallModal.vue';
    // composables
    import { useI18n } from 'vue-i18n';
    import { useClearStorage } from '@/composables/useClearStorage';
    import { usePWAInstall } from '@/composables/usePWAInstall';
    import { Capacitor } from '@capacitor/core';
    import useNotifications from '@/composables/useNotifications';
    import { APP_VERSION } from '@/configs/app';

    const { t } = useI18n();
    const { clearAllStorage, performClearStorage, cancelClearStorage, showDialog, isClearing, showErrorDialog } = useClearStorage();

    const { isPWAInstalled, installPWA, showPwaInstallModal } = usePWAInstall();

    const appVersion = APP_VERSION;

    const notifications = useNotifications();

    const isNotificationsActive = ref(false);

    const settingsButtons = computed(() => [
        ...(isNotificationsActive.value
            ? [
                  {
                      icon: NotificationIcon,
                      text: t('More.notifications'),
                      to: {
                          name: 'Notifications',
                      },
                      onClick: undefined,
                  },
              ]
            : []),
        // {
        //     icon: EmailIcon,
        //     text: t('More.newsletter'),
        //     to: {
        //         name: 'Newsletter',
        //     },
        //     onClick: undefined,
        // },
        {
            icon: ClearIcon,
            text: t('More.clearStorage'),
            to: undefined,
            onClick: clearAllStorage,
        },
        ...(!Capacitor.isNativePlatform() && !isPWAInstalled.value
            ? [
                  {
                      icon: LogoIcon,
                      text: t('More.installApp'),
                      to: undefined,
                      onClick: installPWA,
                      href: undefined,
                      target: undefined,
                  },
              ]
            : []),
    ]);

    const appButtons = computed(() => [
        {
            icon: LogoIcon,
            text: t('More.about'),
            to: undefined,
            onClick: undefined,
            // to: {
            //     name: 'About',
            // },
            href: 'https://24horas.pt/ficha-tecnica/',
            target: '_blank',
        },
        {
            icon: PhoneIcon,
            text: t('More.contact'),
            to: {
                name: 'Contact',
            },
            onClick: undefined,
            href: undefined,
            target: undefined,
        },
        {
            icon: LockIcon,
            text: t('More.legalInformation'),
            to: undefined,
            onClick: undefined,
            href: 'https://24horas.pt/informacao-legal-24horas/',
            target: '_blank',
        },
    ]);

    onMounted(async () => {
        isNotificationsActive.value = await notifications.notificationsActive();
    });
</script>

<style lang="scss">
    .more-btn {
        .v-btn__append {
            margin-left: auto;
        }
        .v-btn__overlay {
            display: none;
        }
    }
</style>
